import { useCallback, useEffect, useState } from 'react';
import { useDeleteSourcesMutation } from '../../../api';
import { handleError } from '../../../lib/helpers';
import { isInSituEligible } from '../tableConfig/tableColumns';
import {
    hasMixedMigrationMethods,
    hasCloudFilesWithOtherSources,
} from '../../../components/MigrationReadinessDialog/utils';

export const useBulkAndDialogs = ({
    notifySuccess,
    notifyError,
    getSourcesByIds,
    bulkDeleteTriggerRef,
    bulkConfigureMigrationTriggerRef,
    bulkInspectSourcesTriggerRef,
    userOrg,
    bulkConfigureInSituTriggerRef,
    preflightTriggerRef,
    setPreflightRequests,
    setPreflightModalConfig,
    setPreflightModalOpen,
}) => {
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
    const [sourcesToDelete, setSourcesToDelete] = useState([]);
    const [highlightedForDeleteId, setHighlightedForDeleteId] = useState(null);

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editSourceId, setEditSourceId] = useState(null);

    const [migrationReadinessDialogOpen, setMigrationReadinessDialogOpen] = useState(false);
    const [selectedSourcesForMigration, setSelectedSourcesForMigration] = useState([]);

    const [sourceInspectionDialogOpen, setSourceInspectionDialogOpen] = useState(false);
    const [selectedSourcesForInspection, setSelectedSourcesForInspection] = useState([]);

    const [inSituReadinessDialogOpen, setInSituReadinessDialogOpen] = useState(false);
    const [selectedSourcesForInSitu, setSelectedSourcesForInSitu] = useState([]);

    const [deleteSources, { isLoading: isDeleting }] = useDeleteSourcesMutation();

    const handleDeleteCancel = useCallback(() => {
        setDeleteConfirmOpen(false);
        setRecordToDelete(null);
        setBulkDeleteMode(false);
        setSourcesToDelete([]);
        setHighlightedForDeleteId(null);
    }, []);

    const handleBulkDeleteTrigger = useCallback((sourceIds) => {
        setBulkDeleteMode(true);
        setSourcesToDelete(sourceIds);
        setDeleteConfirmOpen(true);
    }, []);

    const handleBulkConfigureMigrationTrigger = useCallback(
        (sourceIds) => {
            const selectedSources = getSourcesByIds(sourceIds);
            // Check if sources have mixed migration methods
            if (hasMixedMigrationMethods(selectedSources)) {
                notifyError({
                    description:
                        'Selected sources are for different migration methods and cannot be configured for migration.',
                });
                return;
            }

            // Check if Cloud Files sources are mixed with VM or OS sources
            if (hasCloudFilesWithOtherSources(selectedSources)) {
                notifyError({
                    description: 'Selected sources include Cloud Files sources and can not be migrated together.',
                });
                return;
            }
            setSelectedSourcesForMigration(selectedSources);
            setMigrationReadinessDialogOpen(true);
        },
        [getSourcesByIds, notifyError],
    );

    const handleBulkInspectSourcesTrigger = useCallback(
        (sourceIds) => {
            const selectedSources = getSourcesByIds(sourceIds);

            // Check if sources have mixed migration methods
            if (hasMixedMigrationMethods(selectedSources)) {
                notifyError({
                    description:
                        'Selected sources are for different migration methods and cannot be inspected together.',
                });
                return;
            }

            // Check if Cloud Files sources are mixed with VM or OS sources
            if (hasCloudFilesWithOtherSources(selectedSources)) {
                notifyError({
                    description: 'Selected sources include Cloud Files sources and can not be inspected together.',
                });
                return;
            }
            setSelectedSourcesForInspection(selectedSources);
            setSourceInspectionDialogOpen(true);
        },
        [getSourcesByIds, notifyError],
    );

    const handleBulkConfigureInSituTrigger = useCallback(
        (sourceIds) => {
            const selectedSources = getSourcesByIds(sourceIds);
            const eligible = selectedSources.filter(isInSituEligible);
            if (eligible.length === 0) {
                notifyError({
                    description:
                        'Unable to configure In-Situ Upgrade because the selection contains sources which are not upgradeable and/or have been upgraded already.',
                });
                return;
            }
            if (eligible.length !== selectedSources.length) {
                notifyError({
                    description:
                        'Unable to configure In-Situ Upgrade because the selection contains sources which are not upgradeable and/or have been upgraded already.',
                });
                return;
            }
            setSelectedSourcesForInSitu(eligible);
            setInSituReadinessDialogOpen(true);
        },
        [getSourcesByIds, isInSituEligible, notifyError],
    );

    useEffect(() => {
        if (bulkDeleteTriggerRef) bulkDeleteTriggerRef.current = handleBulkDeleteTrigger;
    }, [bulkDeleteTriggerRef, handleBulkDeleteTrigger]);

    useEffect(() => {
        if (bulkConfigureMigrationTriggerRef)
            bulkConfigureMigrationTriggerRef.current = handleBulkConfigureMigrationTrigger;
    }, [bulkConfigureMigrationTriggerRef, handleBulkConfigureMigrationTrigger]);

    useEffect(() => {
        if (bulkInspectSourcesTriggerRef) bulkInspectSourcesTriggerRef.current = handleBulkInspectSourcesTrigger;
    }, [bulkInspectSourcesTriggerRef, handleBulkInspectSourcesTrigger]);

    useEffect(() => {
        if (bulkConfigureInSituTriggerRef) bulkConfigureInSituTriggerRef.current = handleBulkConfigureInSituTrigger;
    }, [bulkConfigureInSituTriggerRef, handleBulkConfigureInSituTrigger]);

    // Handle preflight trigger for uploaded sources
    const handlePreflightTrigger = useCallback(
        (preflightPayload, migrationContext) => {
            setPreflightRequests(preflightPayload);
            setPreflightModalConfig({
                title: 'Source Upload Preflight Check',
                loadingMessage: 'Initiating Upload Preflight...',
                proceedOnSuccess: migrationContext?.downloadCSV || false,
                type: 'migration',
                csvDownloadPayload: migrationContext?.csvDownloadPayload,
                migrationContext,
            });
            setPreflightModalOpen(true);
        },
        [setPreflightRequests, setPreflightModalConfig, setPreflightModalOpen],
    );

    useEffect(() => {
        if (preflightTriggerRef) preflightTriggerRef.current = handlePreflightTrigger;
    }, [preflightTriggerRef, handlePreflightTrigger]);

    const handleDeleteSource = useCallback(
        async (ids, refetch, setEffectiveSelectedRowKeys) => {
            try {
                const idsToDelete = bulkDeleteMode ? sourcesToDelete : Array.isArray(ids) ? ids : [ids];
                await deleteSources(idsToDelete).unwrap();
                notifySuccess({ description: `Source${idsToDelete.length > 1 ? 's' : ''} deleted successfully` });
                refetch?.();
                setDeleteConfirmOpen(false);
                setRecordToDelete(null);
                setBulkDeleteMode(false);
                setSourcesToDelete([]);
                setHighlightedForDeleteId(null);
                if (bulkDeleteMode && setEffectiveSelectedRowKeys) setEffectiveSelectedRowKeys([]);
            } catch (error) {
                const errorMsg = `Failed to delete source${bulkDeleteMode && sourcesToDelete.length > 1 ? 's' : ''}`;
                handleError(error, notifyError, errorMsg);
                handleDeleteCancel();
            }
        },
        [bulkDeleteMode, sourcesToDelete, deleteSources, notifySuccess, notifyError, handleDeleteCancel],
    );

    const handleDeleteSingleSource = useCallback((sourceId) => {
        setRecordToDelete(sourceId);
        setHighlightedForDeleteId(sourceId);
        setDeleteConfirmOpen(true);
    }, []);

    const handleEditSource = useCallback((sourceId) => {
        setEditSourceId(sourceId);
        setEditModalOpen(true);
    }, []);

    const handleInspectSource = useCallback(
        (sourceId) => {
            const sources = sourceId ? [sourceId] : [];
            const selectedSources = getSourcesByIds(sources);
            setSelectedSourcesForInspection(selectedSources);
            setSourceInspectionDialogOpen(true);
        },
        [getSourcesByIds],
    );

    const handleConfigureInSitu = useCallback(
        (sourceId) => {
            const sources = sourceId ? [sourceId] : [];
            const selectedSources = getSourcesByIds(sources);
            const eligible = selectedSources.filter(isInSituEligible);
            if (eligible.length === 0) {
                notifyError({ description: 'Source is not eligible for In-Situ Upgrade.' });
                return;
            }
            setSelectedSourcesForInSitu(eligible);
            setInSituReadinessDialogOpen(true);
        },
        [getSourcesByIds, isInSituEligible, notifyError],
    );

    return {
        // delete state
        deleteConfirmOpen,
        recordToDelete,
        bulkDeleteMode,
        sourcesToDelete,
        highlightedForDeleteId,
        isDeleting,
        // edit state
        editModalOpen,
        editSourceId,
        // dialogs state
        migrationReadinessDialogOpen,
        selectedSourcesForMigration,
        sourceInspectionDialogOpen,
        selectedSourcesForInspection,
        inSituReadinessDialogOpen,
        selectedSourcesForInSitu,
        // handlers
        handleBulkDeleteTrigger,
        handleBulkConfigureMigrationTrigger,
        handleBulkInspectSourcesTrigger,
        handleBulkConfigureInSituTrigger,
        handleDeleteCancel,
        handleDeleteSource,
        handleDeleteSingleSource,
        handleEditSource,
        handleInspectSource,
        handleConfigureInSitu,
        setRecordToDelete,
        setEditModalOpen,
        setEditSourceId,
        setSelectedSourcesForInspection,
        setSourceInspectionDialogOpen,
        setSelectedSourcesForMigration,
        setMigrationReadinessDialogOpen,
        setHighlightedForDeleteId,
        setSelectedSourcesForInSitu,
        setInSituReadinessDialogOpen,
    };
};
